import Button from "app/components/button/button";
import { campaignIds } from "app/enums";
import { INTEGRATOR } from "app/utils/constants";
import { LiFi } from "app/widgets/LiFi/LiFi";
import { FC, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useFindByIdQuery,
  useGetTotalIdoBalanceQuery,
  useGetUserLeaderboardQuery,
} from "store/api/new.api";
import { useAccount } from "wagmi";
import NotLoggedTimer from "../timer/notlogged-timer";
import RequirementsWidget from "../timer/timer";

import { ONE_MINUTE } from "app/types/constants";
import TokenSaleProgressWidget from "../token-progress-widget/token-progress-widget";
import styles from "./InviteSection.module.scss";

interface InviteSectionProps {
  currentPurchase?: number;
  ticker?: string;
  isAuth: boolean;
  refLink: string;
  theme: "dark" | "light";
  isPreWL: boolean;
}

const InviteSection: FC<InviteSectionProps> = ({
  currentPurchase,
  ticker,
  isAuth,
  isPreWL,
  refLink,
  theme,
}) => {
  const copyTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const { idoId } = useParams<{ idoId: string }>();
  const { address } = useAccount();
  //@ts-ignore
  const { data: userTier, isLoading: userTierLoading } =
    useGetUserLeaderboardQuery(
      { platform: "web3", address: address as string, campaignIds },
      { skip: !isAuth || !address }
    );

  const { data: idoData } = useFindByIdQuery(idoId);

  const { data: totalBalance } = useGetTotalIdoBalanceQuery(idoId, {
    pollingInterval: ONE_MINUTE,
  });

  const hasProgressBar = idoData?.frontEndData?.[35]?.value === "true";

  // Memoize Date objects to prevent infinite re-renders
  const memoizedDates = useMemo(() => {
    if (!idoData) return null;

    return {
      whitelistStartDate: new Date(idoData.preWhitelistDate.start),
      whitelistEndDate: new Date(idoData.preWhitelistDate.end),
      sellStartDate: new Date(idoData.idoDate.start),
      sellEndDate: new Date(idoData.idoDate.end),
    };
  }, [
    idoData?.preWhitelistDate.start,
    idoData?.preWhitelistDate.end,
    idoData?.idoDate.start,
    idoData?.idoDate.end,
  ]);

  const handleCopyLink = () => {
    if (copyTimeout.current) clearTimeout(copyTimeout.current);

    navigator.clipboard
      .writeText(refLink)
      .then(() => {
        setIsCopied(true);
        copyTimeout.current = setTimeout(() => setIsCopied(false), 3000);
      })
      .catch((err) => console.error("Failed to copy link:", err));
  };

  return (
    <div
      className={`${styles.rightSide} ${
        theme === "dark" ? styles.darkTheme : ""
      }`}
    >
      {isAuth && memoizedDates && (
        <>
          <RequirementsWidget
            brevoId={idoData.brevoId}
            totalTokens={idoData.totalTokens}
            totalBalance={0}
            whitelistStartDate={memoizedDates.whitelistStartDate}
            whitelistEndDate={memoizedDates.whitelistEndDate}
            sellStartDate={memoizedDates.sellStartDate}
            sellEndDate={memoizedDates.sellEndDate}
          />
        </>
      )}

      {!isAuth && memoizedDates && (
        <>
          <NotLoggedTimer
            totalTokens={idoData.totalTokens}
            whitelistStartDate={memoizedDates.whitelistStartDate}
            whitelistEndDate={memoizedDates.whitelistEndDate}
            sellStartDate={memoizedDates.sellStartDate}
            sellEndDate={memoizedDates.sellEndDate}
          />
        </>
      )}
      {totalBalance && hasProgressBar && ticker && (
        <TokenSaleProgressWidget
          ticker={ticker}
          softCap={totalBalance?.softCap}
          totalTokens={totalBalance.totalTokens}
          soldTokens={totalBalance.soldTokens}
          currency={"$"}
        />
      )}
      <div className={styles.successSection}>
        <div>
          <p className={styles.balance}>
            My balance:
            <strong>
              &nbsp;{currentPurchase?.toFixed(2)} {ticker ? ticker : ""}
            </strong>
          </p>
          <div className={styles.wrapper}>
            <p
              className={`${styles.wantToText} ${
                theme === "dark" ? styles.darkTheme : ""
              }`}
            >
              Want to increase your gains?
            </p>
          </div>
          <div className={styles.inviteSection}>
            <p>Invite your network</p>
            <p>
              +Get <b>1%</b> from their purchases +
            </p>
            <p>
              +<b>20%</b> from their Karma
            </p>
          </div>
          <div className={styles.btnWrapper}>
            <Button disabled={!isAuth} variant="blue" onClick={handleCopyLink}>
              {isCopied ? "Copied" : "Copy ref link"}
            </Button>
            <LiFi
              integrator={INTEGRATOR}
              tier={userTier?.tier}
              variant="drawer"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteSection;
